import { asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  excludePaidSlots,
  formatServiceToUpdateBookingCart,
  getAttendeesFromCart,
  mapRentalsWithAdditionnalServices,
  TOAST_DURATION,
  updatePriceInfo,
} from 'Components/utils/booking/booking-additional-services/BookingAddtionalServices.utils';
import { getRentalsLayerName } from 'Components/utils/rental/rentals.utils';
import { updateBookingCart } from 'Components/widgets/cart/cart.actions';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import type { ServiceMutationType } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import { RentalsBooking } from './RentalsBooking';
type Props = PropsWithPageTracking<{
  stay: StayEntity;
}>;
export const StayRentalsBooking = ({ stay, tracking, ...rest }: Props) => {
  const [updateInfo, setUpdateInfo] = useState(null);
  const appContext = useAppContext();
  const cartState = useSelector((state) => state.memberSpace.cart.data)?.bookingsCart;
  const dispatch = useDispatch();
  useEffect(() => {
    let timer;

    if (updateInfo) {
      timer = setTimeout(() => setUpdateInfo(null), TOAST_DURATION);
    }

    return () => clearTimeout(timer);
  }, [updateInfo]);
  const bookingId = stay.booking.id;
  const upsells = stay?.upsells;
  const rentals = stay.product?.rentalsTree || [];
  const cartBooking = cartState?.cartBookings?.find((b) => b.bookingId === bookingId);
  const stayService = (cartBooking?.stayServices || []).find(
    ({ stayIndex }) => stayIndex === stay.stayIndex,
  );
  const cartRentals: Array<CartRental> = stayService?.rentals || [];
  const availableRentalsUpdatedWithCart: ReadonlyArray<UpsellRental> = [
    ...(upsells?.rentals || []),
  ].map((rental) => ({
    ...rental,
    pricingInfo: updatePriceInfo(rental.pricingInfo, cartRentals, appContext.locale),
  }));
  const rentalsWithAdditionalServices = mapRentalsWithAdditionnalServices(
    rentals,
    availableRentalsUpdatedWithCart,
    {
      filtered: true,
    },
  );
  return (
    <RentalsBooking
      {...asWidgetProps<ComponentProps<typeof RentalsBooking>>({
        ...rest,
        contextValue: {
          attendeesList: getAttendeesFromCart(cartBooking),
          bookingId,
          hideAgeServiceSlot: true,
          onlyBookableServices: true,
          setUpdateInfo,
          showRentAllSchedules: true,
          updateInfo,
          updateService: (
            updatedService: ServiceMutationType,
            pricingInfo: PricingInfoWithScheduleType,
            serviceIds: ReadonlyArray<string>,
          ) => {
            const updatedCart = formatServiceToUpdateBookingCart({
              appContext,
              bookingId,
              serviceIds,
              type: pricingInfo.type,
              updatedService: excludePaidSlots(updatedService, upsells.rentals),
              stayIndex: stay?.stayIndex,
            });
            const schedules = updatedCart?.[0]?.schedules?.filter(
              ({ attendees }) => attendees.length,
            );
            setUpdateInfo({
              itemsNumber: updatedService.length,
              message: schedules.length
                ? t('booking.activities.skiEquipment.newEquipmentAdded')
                : t('booking.activities.skiEquipment.newEquipmentRemoved'),
            });
            return dispatch(updateBookingCart(updatedCart[0]));
          },
        },
        layerName: getRentalsLayerName(stay.booking.id, stay.id),
        rentals: rentalsWithAdditionalServices,
        tracking: tracking,
      })}
    />
  );
};
